import { Box, Grid, Typography } from '@mui/material'
import moment from 'moment'
import { FC } from 'react'

// others
import { BillStatus, ExternalUserCaseRole, FetchBillDetailsQuery } from '../../../../generated'
import { STANDARD_DATE_FORMAT } from '../../../constants'
import { formatCurrency } from '../../../utils'
import { CardBox } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { Link } from 'react-router-dom'

type Case = FetchBillDetailsQuery['fetchBillDetails']['bill']['case']
type CaseToExternalUserToExternalUserRole = NonNullable<Case>['caseToExternalUserToExternalUserRole']

interface BillDetailsCardProps {
  bill: {
    id?: number
    status?: BillStatus
    amount?: number
    amountPaid?: number | null
    dueDate?: string
    issueDate?: string
    case: {
      id?: string
      caseNumber?: string | null
      title?: string | null
      caseToExternalUserToExternalUserRole?: CaseToExternalUserToExternalUserRole | null
    }
  }
}

const BillDetailsCard: FC<BillDetailsCardProps> = ({ bill }) => {
  const { amount, dueDate, issueDate, case: caseData } = bill
  const { caseNumber, title } = caseData || {}
  const client = caseData?.caseToExternalUserToExternalUserRole?.find(
    externalUserRole => externalUserRole.caseRole === ExternalUserCaseRole.PrimaryClient
  )?.externalUserToExternalUserRole?.externalUser

  const renderClientInfo = () => {
    if (!client) return ''

    return (
      <span>
        {client.displayName || `${client.firstName || ''} ${client.lastName || ''}`.trim()} (
        <Link
          // The route param IS the client number — it is passed straight to
          // getClientByClientNumber. A "-1111" suffix was appended here, so the lookup never
          // matched and the client page sat on "Loading..." forever. Every other link to a
          // client uses the bare number.
          to={`/contacts/client/${client.clientNumber}`}
          style={{
            color: '#771117'
          }}
        >
          #{client.clientNumber}
        </Link>
        )
      </span>
    )
  }

  const renderMatterInfo = () => {
    if (!title) return ''

    return (
      <span>
        {title} (
        <Link
          to={`/matters/matter-detail/${caseData?.id}`}
          style={{
            color: '#771117'
          }}
        >
          #{caseNumber}
        </Link>
        )
      </span>
    )
  }

  const detailItems = [
    {
      label: 'Client',
      value: renderClientInfo()
    },
    {
      label: 'Matter',
      value: renderMatterInfo()
    },
    {
      label: 'Issue Date',
      value: issueDate ? moment(issueDate).format(STANDARD_DATE_FORMAT) : ''
    },
    // A due date only exists once the bill is finalized, so the row is dropped entirely on a
    // pre-bill rather than shown blank.
    ...(dueDate
      ? [
          {
            label: 'Due Date',
            value: moment(dueDate).format(STANDARD_DATE_FORMAT)
          }
        ]
      : []),
    {
      label: 'Bill Total',
      value: amount !== undefined ? formatCurrency(amount) : ''
    }
  ]

  return (
    <CardBox mb={3}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E4E7EC'
        }}
      >
        <Typography variant="h6" fontWeight={400} color="gray900" fontSize="18px">
          Details
        </Typography>
      </Box>

      {detailItems.map((item, index) => (
        <Grid
          container
          key={item.label}
          sx={{
            ...flexCenter,
            p: 3,
            borderBottom: index < detailItems.length - 1 ? '1px solid #E4E7EC' : 'none'
          }}
        >
          <Grid item xs={6}>
            <Typography fontWeight="bold" fontSize="18px" sx={{ width: '50%' }}>
              {item.label}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography fontSize="18px" color="#0A071B">
              {item.value}
            </Typography>
          </Grid>
        </Grid>
      ))}
    </CardBox>
  )
}

export default BillDetailsCard
